import { useIntl } from 'react-intl';
import moment from 'moment';
import { Checkbox, Col, DatePicker, Row, Select } from 'antd';
import { WorkOrderMigrateConsultListRequest } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementStatus,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import { CompaniesInfiniteSelectField, InputDate } from '@/components/_common';
import { DateTimeFormat } from '@/utils/moment';
import WorkOrderSystemOriginSelect from '@/components/_common/WorkOrderSystemOriginSelect';
import { isAppPortocar } from '@/index';

export default function MigrationConsultFilter({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<WorkOrderMigrateConsultListRequest>) {
  const intl = useIntl();

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <CompaniesInfiniteSelectField
          onChange={(value) => {
            setQueryParams((prev) => ({ ...prev, companyId: value }));
          }}
          value={queryParams?.companyId}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.company',
          })}
          allowClear
          queryFilter={{ rows: 50 }}
          queryEnable={!!queryParams?.companyId}
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.estimatedLeaveDate',
          })}
          value={
            queryParams?.estimatedLeaveDate
              ? moment(queryParams?.estimatedLeaveDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              estimatedLeaveDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.entryDate',
          })}
          value={
            queryParams?.entryDate ? moment(queryParams?.entryDate) : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              entryDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.authorizationDate',
          })}
          value={
            queryParams?.authorizationDate
              ? moment(queryParams?.authorizationDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              authorizationDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.closingDate',
          })}
          value={
            queryParams?.closingDate
              ? moment(queryParams?.closingDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              closingDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={4} xxl={4}>
        <DatePicker
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.returnDate',
          })}
          value={
            queryParams?.returnDate
              ? moment(queryParams?.returnDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              returnDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.status}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, status: value }))
          }
          placeholder="Situação"
        >
          <Select.Option value={WorkOrderComplementStatus.Open}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.open',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatus.Authorized}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.authorized',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatus.Closed}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.closed',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementStatus.Cancelled}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_status.cancelled',
            })}
          </Select.Option>
        </Select>
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.type}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, type: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.type',
          })}
        >
          <Select.Option value={WorkOrderComplementType.Courtesy}>
            {intl.formatMessage({
              id: `workOrders.domain.enums.work_order_type.${
                isAppPortocar ? 'insideCourtesy' : 'courtesy'
              }`,
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementType.Warranty}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.warranty',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementType.WorkOrder}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.work_order',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementType.TotalLoss}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.total_loss',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementType.Return}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.return',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementType.Revision}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.revision',
            })}
          </Select.Option>
        </Select>
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <WorkOrderSystemOriginSelect
          placeholder="Sistema de origem"
          value={queryParams?.originSystemIds}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              originSystemIds: value,
            }))
          }
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Checkbox
          value={queryParams?.hasLinkedWorkOrders}
          onChange={({ target }) =>
            setQueryParams((prev) => ({
              ...prev,
              hasLinkedWorkOrders: target.checked,
            }))
          }
        >
          Filtrar orçamentos vinculados
        </Checkbox>
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <OrderExtraFilter
          optionsValue={{ asc: 0, desc: 1 }}
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>
    </Row>
  );
}
